/**
 * Family All-Day Section Component
 *
 * Displays all-day events in a section above the hourly timeline.
 * Each column corresponds to a family member resource.
 *
 * Follows single responsibility principle: only renders all-day events section.
 */

import { useColorScheme } from '@/hooks/use-color-scheme';
import { ResourcesCalendarTheme } from '@/lib/calendar/ResourcesCalendarTheme';
import type { FamilyMemberResource, AllDayEventsByFamilyMember } from '@/types/family-resource';
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Colors } from '@/constants/theme';
import { getHeadingFont } from '@/constants/typography';

/**
 * Determines text color (white or black) based on background color brightness
 */
function getContrastTextColor(hexColor: string): '#FFFFFF' | '#000000' {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const luminance = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const L = 0.2126 * luminance(r) + 0.7152 * luminance(g) + 0.0722 * luminance(b);
  return L > 0.5 ? '#000000' : '#FFFFFF';
}

type FamilyAllDaySectionProps = {
  visibleResources: FamilyMemberResource[];
  allDayEventsByMember: AllDayEventsByFamilyMember;
  onEventPress?: (eventId: string) => void;
  hourWidth: number;
  columnWidth: number;
  borderColor: string;
};

export const FamilyAllDaySection = React.memo(function FamilyAllDaySection({
  visibleResources,
  allDayEventsByMember,
  onEventPress,
  hourWidth,
  columnWidth,
  borderColor,
}: FamilyAllDaySectionProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const styles = ResourcesCalendarTheme.getAllDaySectionStyles(colorScheme ?? 'light');

  return (
    <View style={[styles.container, { borderBottomWidth: 1, borderBottomColor: palette.border }]}>
      <View style={localStyles.scrollContent}>
        {/* Time column with "All day" label */}
        <View style={[localStyles.timeColumn, { width: hourWidth }]}>
          <Text style={[localStyles.allDayLabel, { color: palette.textMuted, fontFamily: getHeadingFont() }]}>
            All day
          </Text>
        </View>

        {/* Family member columns */}
        {visibleResources.map((resource) => {
          const events = allDayEventsByMember.get(resource.id) || [];

          return (
            <View
              key={resource.id}
              style={[localStyles.resourceColumn, { width: columnWidth }]}
            >
              {events.length === 0 ? (
                <Text style={styles.emptyText}>-</Text>
              ) : (
                events.map(event => {
                  const textColor = getContrastTextColor(resource.color);
                  return (
                    <TouchableOpacity
                      key={event.id}
                      style={[
                        styles.eventChip,
                        { backgroundColor: resource.color },
                      ]}
                      onPress={() => onEventPress?.(event.id)}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.eventText, { color: textColor }]} numberOfLines={1}>
                        {event.title}
                      </Text>
                    </TouchableOpacity>
                  );
                })
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
});

const localStyles = StyleSheet.create({
  scrollContent: {
    flexDirection: 'row',
  },
  timeColumn: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  allDayLabel: {
    fontSize: 11,
    fontWeight: '500',
  },
  resourceColumn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
});
